// Member Signup Checkbox Web Component
var MemberSignupCheckbox = class extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="checkbox"].checkbox');

    if (this.input) {
      this.input.addEventListener('change', this._onCheckboxChange.bind(this));
    }

    // Prevent accordion toggle when clicking on checkbox area
    this.addEventListener('click', this._preventAccordionToggle.bind(this));
  }

  _preventAccordionToggle(event) {
    // Stop event from bubbling up to the summary element
    event.stopPropagation();
  }

  async _onCheckboxChange(event) {
    const isChecked = event.target.checked;
    const attributeValue = isChecked ? 'true' : '';

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            new_member: attributeValue
          }
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const cartContent = await response.json();

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
      }));

      // For main cart page, also reload the page to show updated member pricing
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }

    } catch (error) {
      console.error('Error updating member signup:', error);
      // Revert checkbox state on error
      event.target.checked = !isChecked;
    }
  }
};

// Tax Toggle Web Component
var TaxToggle = class extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('input[type="checkbox"]');

    if (this.toggle) {
      this.toggle.addEventListener('change', this._onToggleChange.bind(this));
    }
  }

  async _onToggleChange(event) {
    const isChecked = event.target.checked;
    const attributeValue = isChecked ? 'true' : '';

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            taxes_excluded: attributeValue
          }
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Reload page to reflect tax changes in Liquid rendering
      window.location.reload();

    } catch (error) {
      console.error('Error updating tax toggle:', error);
      // Revert toggle state on error
      event.target.checked = !isChecked;
    }
  }
};

// Customer Type Selector Web Component
class CustomerTypeSelector extends HTMLElement {
  constructor() {
    super();
    this.context = this.getAttribute('data-context') || 'cart';
    this.selectedType = null;
    this.modal = null;
  }

  connectedCallback() {
    // DOM is ready, now we can initialize
    this.init();
  }

  init() {
    this.radioButtons = this.querySelectorAll('input[type="radio"]');
    this.checkoutButtons = this.getCheckoutButtons();

    // If no radio buttons exist (customer is logged in), enable checkout immediately
    if (this.radioButtons.length === 0) {
      this.enableCheckout();
      return;
    }

    // Determine initial state
    if (this.hasB2BSignupCompleted()) {
      // Pre-select B2B option if signup was completed
      const b2bRadio = this.querySelector('input[value="b2b"]');
      if (b2bRadio) {
        b2bRadio.checked = true;
        this.selectedType = 'b2b';
      }
    } else {
      // Check if any radio button is already checked in HTML
      const checkedRadio = this.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        this.selectedType = checkedRadio.value;
      }
    }

    // Add event listeners
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', this.handleSelectionChange.bind(this));
    });

    // Intercept checkout button clicks
    this.interceptCheckoutButtons();

    // Enable or disable checkout based on selection state
    if (this.selectedType) {
      this.enableCheckout();
    } else {
      this.disableCheckout();
    }
  }

  hasB2BSignupCompleted() {
    try {
      const data = sessionStorage.getItem('b2b_signup_data');
      if (!data) return false;

      const signupData = JSON.parse(data);
      return signupData.completed === true;
    } catch (error) {
      return false;
    }
  }

  getCheckoutButtons() {
    if (this.context === 'drawer') {
      return document.querySelectorAll('#cart-drawer button[name="checkout"], #cart-drawer a[href*="checkout"]');
    } else {
      return document.querySelectorAll('button[name="checkout"], .cart-form button[type="submit"]');
    }
  }

  disableCheckout() {
    this.checkoutButtons.forEach(button => {
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
    });
  }

  enableCheckout() {
    this.checkoutButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '';
      button.style.cursor = '';
    });
  }

  handleSelectionChange(event) {
    this.selectedType = event.target.value;
    this.clearError();

    if (this.selectedType === 'private') {
      // Clear any B2B data when switching to private
      this.clearB2BData();
      this.enableCheckout();
    } else if (this.selectedType === 'b2b') {
      // Check if B2B signup already completed (sessionStorage exists)
      if (this.hasB2BSignupCompleted()) {
        // Don't open modal, just enable checkout
        this.enableCheckout();
      } else {
        // Not completed yet, open modal for signup
        this.openB2BModal();
      }
    }
  }

  clearB2BData() {
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('b2b_signup_data');
    } catch (error) {
      console.error('Error clearing B2B sessionStorage:', error);
    }

    // Clear VAT ID from cart attributes
    this.clearVatIdFromCart();
  }

  async clearVatIdFromCart() {
    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            vat_id: '',
            vat_invalid: ''
          }
        })
      });

      // Don't trigger cart:refresh event here to avoid re-rendering the customer type selector
      // The VAT ID is not visible in the UI, so no refresh needed
    } catch (error) {
      console.error('Error clearing VAT ID from cart:', error);
    }
  }

  openB2BModal() {
    // Disable checkout until B2B form is completed
    this.disableCheckout();
    // Create or show B2B modal
    if (!this.modal) {
      this.createB2BModal();
    }
    this.modal.show();
  }

  createB2BModal() {
    // Find pre-rendered B2B modal in the DOM (from overlay-group)
    this.modal = document.querySelector('#b2b-modal');

    if (this.modal) {

      // Listen for modal completion
      this.modal.addEventListener('b2b-completed', () => {
        this.enableCheckout();
      });

      // Listen for modal cancellation
      this.modal.addEventListener('b2b-cancelled', () => {
        this.clearB2BData();
        this.resetSelection();
      });
    } else {
      console.error('B2B modal not found. Please add b2b-signup-modal section to overlay-group.');
    }
  }

  resetSelection() {
    this.radioButtons.forEach(radio => radio.checked = false);
    this.selectedType = null;
    this.disableCheckout();
  }

  interceptCheckoutButtons() {
    this.checkoutButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        if (!this.selectedType) {
          event.preventDefault();
          this.showError();
        }
      });
    });
  }

  showError() {
    this.classList.add('customer-type-selector--error');
  }

  clearError() {
    this.classList.remove('customer-type-selector--error');
  }
}


// B2B Modal Web Component - Extends theme's Drawer class
// Handles B2B signup form submission to backend API
class B2BModal extends window.Drawer {
  constructor() {
    super();
    this.skipVatValidation = false;
  }

  connectedCallback() {
    super.connectedCallback();

    // Get form reference
    this.form = this.querySelector('.b2b-signup-form');

    // Initialize form handling when modal is shown
    this.addEventListener('dialog:after-show', () => {
      this.formCompleted = false;
      this.skipVatValidation = false;
      this.clearFormErrors();
      this.repopulateFormData();
    });

    // Setup form submission handler
    this.setupFormHandler();

    // Setup VAT error UI button handlers
    this.setupVATErrorHandlers();

    // Check for URL parameter to open modal programmatically
    this.checkURLParameter();
  }

  get openFrom() {
    return 'right';
  }

  async hide() {
    if (this.isHiding) return;
    this.isHiding = true;

    const eventType = this.formCompleted ? 'b2b-completed' : 'b2b-cancelled';
    this.dispatchEvent(new CustomEvent(eventType, { bubbles: true }));

    this.formCompleted = false;
    this.hideVATError();

    const result = await super.hide();
    this.isHiding = false;

    return result;
  }

  setupFormHandler() {
    if (!this.form) return;

    // Prevent duplicate event listeners if connectedCallback is called multiple times
    if (this.form.dataset.handlerAttached) return;
    this.form.dataset.handlerAttached = 'true';

    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Clear previous errors
      this.clearFormErrors();

      // Get form data
      const formData = new FormData(this.form);
      const email = formData.get('email')?.trim();
      const companyName = formData.get('company_name')?.trim();
      const vatId = formData.get('vat_id')?.trim();

      // Client-side validation
      if (!email) {
        this.showFormError(window.themeVariables?.strings?.formErrorEmailRequired || 'Email is required');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.showFormError(window.themeVariables?.strings?.formErrorEmailInvalid || 'Please enter a valid email address');
        return;
      }

      if (!companyName) {
        this.showFormError(window.themeVariables?.strings?.formErrorCompanyRequired || 'Company name is required');
        return;
      }

      if (!vatId) {
        this.showFormError(window.themeVariables?.strings?.formErrorVatRequired || 'VAT ID is required');
        return;
      }

      // Validate VAT country code prefix (client-side)
      const countryValidation = this.validateVATCountryCode(vatId);
      if (!countryValidation.valid) {
        this.showVATCountryError(this.form.querySelector('#b2b-vat-id'), countryValidation.error);
        return;
      }

      // Store values for later use
      this.submittedVatId = vatId;
      this.submittedEmail = email;

      // Set loading state
      this.setLoading(true);

      try {
        const response = await fetch('/apps/integration/customer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            company_name: companyName,
            vat_id: vatId,
            skip_vat_validation: this.skipVatValidation
          })
        });

        const result = await response.json();

        // 200 = Success - customer was created/updated
        if (response.ok) {
          await this.handleSuccessfulSubmission(result);
          return;
        }

        // 422 = VAT issues (invalid VAT or VAT service errors) - show "Try Again / Continue Anyway"
        if (response.status === 422) {
          this.showVATError(result.errorCode || null, result.error || null);
          return;
        }

        // 400 = Hard errors (Shopify failures, missing params) - show form error
        this.showFormError(result.error || 'An error occurred. Please try again.');

      } catch (error) {
        console.error('B2B signup error:', error);
        this.showFormError(window.themeVariables?.strings?.formErrorGeneric || 'An error occurred. Please try again.');
      } finally {
        this.setLoading(false);
      }
    });

    // Clear VAT country error when user types in VAT field
    const vatField = this.form.querySelector('#b2b-vat-id');
    if (vatField) {
      vatField.addEventListener('input', () => {
        this.clearVATCountryError(vatField);
        this.skipVatValidation = false;
      });
    }

    // Save form data as user types (for repopulation if modal closes)
    const formFields = this.form.querySelectorAll('input');
    formFields.forEach(field => {
      field.addEventListener('input', () => {
        this.saveFormData();
      });
    });
  }

  async handleSuccessfulSubmission(result) {
    this.formCompleted = true;
    this.setB2BSignupCompleted();

    // Save VAT ID to cart attributes
    await this.saveVatIdToCart(result.vatValid);

    // Redirect to checkout
    this.redirectToCheckout();
  }

  setLoading(isLoading) {
    const submitBtn = this.form?.querySelector('.b2b-signup-form__submit');
    const submitText = this.form?.querySelector('.b2b-signup-form__submit-text');
    const loadingText = this.form?.querySelector('.b2b-signup-form__submit-loading');

    if (submitBtn) {
      submitBtn.disabled = isLoading;
    }
    if (submitText) {
      submitText.style.display = isLoading ? 'none' : '';
    }
    if (loadingText) {
      loadingText.style.display = isLoading ? '' : 'none';
    }
  }

  showFormError(message) {
    const errorContainer = this.form?.querySelector('.b2b-signup-form__error');
    const errorMessage = this.form?.querySelector('.b2b-signup-form__error-message');

    if (errorContainer && errorMessage) {
      errorMessage.textContent = message;
      errorContainer.style.display = 'block';
    }
  }

  clearFormErrors() {
    const errorContainer = this.form?.querySelector('.b2b-signup-form__error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }

    // Clear VAT country error if present
    const vatField = this.form?.querySelector('#b2b-vat-id');
    if (vatField) {
      this.clearVATCountryError(vatField);
    }
  }

  redirectToCheckout() {
    let checkoutUrl = window.Shopify.routes.root + 'checkout';

    if (this.submittedEmail) {
      checkoutUrl += `?checkout[email]=${encodeURIComponent(this.submittedEmail)}`;
    }

    window.location.href = checkoutUrl;
  }

  async saveVatIdToCart(vatValid) {
    if (!this.submittedVatId) return;

    try {
      const attributes = {
        vat_id: this.submittedVatId
      };

      // Set vat_invalid attribute if VAT was not valid (user bypassed validation)
      if (!vatValid) {
        attributes.vat_invalid = 'true';
      }

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('Error saving VAT ID to cart:', error);
    }
  }

  setupVATErrorHandlers() {
    // Prevent duplicate event listeners
    if (this.vatErrorHandlersAttached) return;
    this.vatErrorHandlersAttached = true;

    const tryAgainBtn = this.querySelector('.b2b-modal__try-again');
    const continueAnywayBtn = this.querySelector('.b2b-modal__continue-anyway');

    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', () => {
        this.hideVATError();
      });
    }

    if (continueAnywayBtn) {
      continueAnywayBtn.addEventListener('click', () => {
        // Set skip flag and re-submit form
        this.skipVatValidation = true;
        this.hideVATError();
        this.form?.requestSubmit();
      });
    }
  }

  showVATError(errorCode = null, errorMessage = null) {
    const formContainer = this.querySelector('.b2b-modal__form-container');
    const errorContainer = this.querySelector('.b2b-modal__vat-error');
    const additionalErrorEl = this.querySelector('.b2b-modal__vat-error-details');

    if (formContainer) formContainer.style.display = 'none';
    if (errorContainer) errorContainer.style.display = 'block';

    if (additionalErrorEl) {
      const translatedError = this.getVATErrorMessage(errorCode);
      if (translatedError) {
        additionalErrorEl.textContent = translatedError;
        additionalErrorEl.style.display = 'block';
      } else {
        additionalErrorEl.textContent = '';
        additionalErrorEl.style.display = 'none';
      }
    }
  }

  getVATErrorMessage(errorCode) {
    if (!errorCode) return null;

    const errorMessages = {
      'INVALID_INPUT': window.themeVariables?.strings?.vatErrorInvalidInput,
      'INVALID_VAT': window.themeVariables?.strings?.vatErrorInvalidInput,
      'SERVICE_UNAVAILABLE': window.themeVariables?.strings?.vatErrorServiceUnavailable,
      'DATABASE_UNAVAILABLE': window.themeVariables?.strings?.vatErrorDatabaseUnavailable
    };

    return errorMessages[errorCode] || null;
  }

  hideVATError() {
    const formContainer = this.querySelector('.b2b-modal__form-container');
    const errorContainer = this.querySelector('.b2b-modal__vat-error');
    const additionalErrorEl = this.querySelector('.b2b-modal__vat-error-details');

    if (formContainer) formContainer.style.display = '';
    if (errorContainer) errorContainer.style.display = 'none';
    if (additionalErrorEl) {
      additionalErrorEl.textContent = '';
      additionalErrorEl.style.display = 'none';
    }
  }

  checkURLParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('b2b_signup')) {
      setTimeout(() => {
        this.show();
      }, 500);

      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('b2b_signup');
      window.history.replaceState({}, '', newUrl);
    }
  }

  // Session storage helpers
  getB2BSignupData() {
    try {
      const data = sessionStorage.getItem('b2b_signup_data');
      if (!data) return { completed: false };
      return JSON.parse(data);
    } catch (error) {
      return { completed: false };
    }
  }

  saveB2BSignupData(updates) {
    try {
      const current = this.getB2BSignupData();
      const merged = { ...current, ...updates };
      sessionStorage.setItem('b2b_signup_data', JSON.stringify(merged));
    } catch (error) {
      console.error('Error saving B2B signup data:', error);
    }
  }

  setB2BSignupCompleted() {
    this.saveB2BSignupData({ completed: true });
  }

  saveFormData() {
    if (!this.form) return;

    const formData = {
      email: this.form.querySelector('#b2b-email')?.value || '',
      company_name: this.form.querySelector('#b2b-company-name')?.value || '',
      vat_id: this.form.querySelector('#b2b-vat-id')?.value || ''
    };

    this.saveB2BSignupData({ formData });
  }

  repopulateFormData() {
    if (!this.form) return;

    const signupData = this.getB2BSignupData();
    const formData = signupData.formData;
    if (!formData) return;

    const emailField = this.form.querySelector('#b2b-email');
    const companyField = this.form.querySelector('#b2b-company-name');
    const vatField = this.form.querySelector('#b2b-vat-id');

    if (emailField && formData.email) emailField.value = formData.email;
    if (companyField && formData.company_name) companyField.value = formData.company_name;
    if (vatField && formData.vat_id) vatField.value = formData.vat_id;
  }

  // VAT country code validation (client-side prefix check)
  validateVATCountryCode(vatNumber) {
    const shippingCountries = this.getAttribute('data-shipping-countries');
    if (!shippingCountries) {
      return { valid: true, countryCode: '', error: null };
    }

    const enabledCountries = shippingCountries.split(',').map(c => c.trim().toUpperCase());
    const cleanVAT = vatNumber.replace(/\s/g, '').toUpperCase();

    if (cleanVAT.length < 2) {
      const error = window.themeVariables?.strings?.vatCountryMinLength || 'VAT number must be at least 2 characters';
      return { valid: false, countryCode: '', error };
    }

    const vatCountryCode = cleanVAT.substring(0, 2);

    // Special handling for GB/XI (UK and Northern Ireland)
    const hasGB = enabledCountries.includes('GB');
    if (hasGB && (vatCountryCode === 'GB' || vatCountryCode === 'XI')) {
      return { valid: true, countryCode: vatCountryCode, error: null };
    }

    // Special handling for Greece (country code GR, but VAT prefix is EL)
    const hasGR = enabledCountries.includes('GR');
    if (hasGR && vatCountryCode === 'EL') {
      return { valid: true, countryCode: vatCountryCode, error: null };
    }

    if (!enabledCountries.includes(vatCountryCode)) {
      const allowedPrefixes = [...enabledCountries];
      // Add XI after GB for display
      if (hasGB) {
        const gbIndex = allowedPrefixes.indexOf('GB');
        allowedPrefixes.splice(gbIndex + 1, 0, 'XI');
      }
      // Replace GR with EL for display (Greek VAT numbers use EL prefix)
      if (hasGR) {
        const grIndex = allowedPrefixes.indexOf('GR');
        allowedPrefixes[grIndex] = 'EL';
      }

      let error;
      if (allowedPrefixes.length === 1) {
        const message = window.themeVariables?.strings?.vatCountryInvalidSingle || 'VAT number must start with {{country}}';
        error = message.replace('{{country}}', allowedPrefixes[0]);
      } else {
        const message = window.themeVariables?.strings?.vatCountryInvalidMultiple || 'VAT number must start with one of: {{countries}}';
        error = message.replace('{{countries}}', allowedPrefixes.join(', '));
      }
      return { valid: false, countryCode: vatCountryCode, error };
    }

    return { valid: true, countryCode: vatCountryCode, error: null };
  }

  showVATCountryError(vatField, errorMessage) {
    if (!vatField) return;

    let errorElement = vatField.parentElement.querySelector('.vat-country-error');

    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'vat-country-error';
      errorElement.style.cssText = 'color: #d72c0d; font-size: 0.875rem; margin-top: 0.5rem;';
      vatField.parentElement.appendChild(errorElement);
    }

    errorElement.textContent = errorMessage;
    vatField.setAttribute('aria-invalid', 'true');
  }

  clearVATCountryError(vatField) {
    if (!vatField) return;

    const errorElement = vatField.parentElement.querySelector('.vat-country-error');
    if (errorElement) {
      errorElement.remove();
    }
    vatField.removeAttribute('aria-invalid');
  }
}

// Register web components
if (!window.customElements.get("member-signup-checkbox")) {
  window.customElements.define("member-signup-checkbox", MemberSignupCheckbox);
}

if (!window.customElements.get("customer-type-selector")) {
  window.customElements.define("customer-type-selector", CustomerTypeSelector);
}

if (!window.customElements.get("b2b-modal")) {
  window.customElements.define("b2b-modal", B2BModal);
}

if (!window.customElements.get("tax-toggle")) {
  window.customElements.define("tax-toggle", TaxToggle);
}


// Product Image Galleries Component
import { PhotoSwipeLightbox } from "vendor";

class ProductImageGalleries extends HTMLElement {
  constructor() {
    super();
    this.galleries = [];
    this._photoswipe = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Initialize each gallery
    this.querySelectorAll('.product-image-gallery').forEach((gallery, index) => {
      this.initializeGallery(gallery, index);
    });
  }

  initializeGallery(galleryElement, index) {
    const galleryType = galleryElement.getAttribute('data-gallery-type');
    const previewItems = galleryElement.querySelectorAll('.product-image-gallery__preview-item');
    const dataItems = galleryElement.querySelectorAll('.product-image-gallery__data-item');

    // Create data source for PhotoSwipe from the server-rendered data
    const dataSource = Array.from(dataItems).map((item, index) => {
      // Find the corresponding thumbnail image for msrc and dimensions
      const thumbnailImg = previewItems[index]?.querySelector('.product-image-gallery__preview-image');

      return {
        src: item.getAttribute('data-src'),
        msrc: thumbnailImg ? (thumbnailImg.currentSrc || thumbnailImg.src) : item.getAttribute('data-src'),
        alt: item.getAttribute('data-alt') || '',
        width: 0, // Will be set after thumbnails load
        height: 0, // Will be set after thumbnails load
        thumbnailElement: thumbnailImg || null
      };
    });

    // Wait for thumbnail images to load, then update dimensions
    this.waitForThumbnailsAndUpdateDimensions(dataSource, previewItems);

    // Add click handlers to preview items
    previewItems.forEach((item, itemIndex) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.openGallery(dataSource, itemIndex, galleryType);
      });
    });

    // Store gallery data for potential future use
    this.galleries[index] = {
      element: galleryElement,
      type: galleryType,
      dataSource: dataSource
    };
  }

  waitForThumbnailsAndUpdateDimensions(dataSource, previewItems) {
    // Get first 3 thumbnail images
    const thumbnailImages = Array.from(previewItems)
      .slice(0, 3)
      .map(item => item.querySelector('.product-image-gallery__preview-image'))
      .filter(img => img);

    if (thumbnailImages.length === 0) return;

    // Wait for each thumbnail to load
    const loadPromises = thumbnailImages.map((img, index) => {
      return new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
          // Image already loaded
          resolve({ index, width: img.naturalWidth, height: img.naturalHeight });
        } else {
          // Wait for image to load
          img.onload = () => {
            resolve({ index, width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            resolve({ index, width: 0, height: 0 }); // Fallback
          };
        }
      });
    });

    // Update dataSource when thumbnails are loaded
    Promise.all(loadPromises).then((results) => {
      results.forEach(({ index, width, height }) => {
        if (dataSource[index] && width > 0 && height > 0) {
          dataSource[index].width = width;
          dataSource[index].height = height;
        }
      });
    });
  }

  // Create PhotoSwipe instance once (like the theme does)
  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }

    const photoswipe = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables?.strings?.closeGallery || 'Close gallery',
      zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
      errorMsg: window.themeVariables?.strings?.errorGallery || 'Image cannot be loaded',
      // UX settings to match theme
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    // Handle unknown image sizes for images beyond the first 3 (fallback only)
    photoswipe.on('gettingData', (item) => {
      // The slide data is directly in item.data
      const slideData = item.data;

      // Skip if image already has dimensions (from thumbnails or background loading)
      if (slideData.width > 0 && slideData.height > 0) {
        return;
      }

      if (slideData.width < 1 || slideData.height < 1) { // unknown size
        const img = new Image();

        img.onload = function() {
          slideData.width = this.width; // set image width
          slideData.height = this.height; // set image height

          // Update PhotoSwipe with new dimensions (PhotoSwipe v5 API)
          if (photoswipe.pswp) {
            // Force PhotoSwipe to recalculate dimensions
            photoswipe.pswp.refreshSlideContent(photoswipe.pswp.currIndex);
            photoswipe.pswp.updateSize();
          }
        };

        img.onerror = function() {
          // Set fallback dimensions
          slideData.width = 800;
          slideData.height = 600;
        };

        img.src = slideData.src; // let's download image
      }
    });

    // Add bottom navigation bar (pagination) - same as theme
    photoswipe.on('uiRegister', () => {
      photoswipe.pswp.ui.registerElement({
        name: 'bottom-bar',
        order: 5,
        appendTo: 'wrapper',
        html: `
          <div class="pagination">
            <button class="pagination__item group" rel="prev">
              <span class="animated-arrow animated-arrow--reverse"></span>
            </button>
            <span class="pagination__current text-sm">
              <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
            </span>
            <button class="pagination__item group" rel="next">
              <span class="animated-arrow"></span>
            </button>
          </div>
        `,
        onInit: (el, pswp) => {
          el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]')?.addEventListener('click', () => pswp.prev());
          el.querySelector('[rel="next"]')?.addEventListener('click', () => pswp.next());
          pswp.on('change', () => {
            el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
          });
        }
      });
    });

    photoswipe.init();
    return this._photoswipe = photoswipe;
  }

  openGallery(dataSource, startIndex = 0, galleryType = '') {
    // Use the dataSource as-is (dimensions already set in initializeGallery)
    // First 3 images have thumbnail dimensions, rest have 0,0 for background loading
    this.photoswipe.loadAndOpen(startIndex, dataSource);

    // Background load dimensions for remaining images (4+)
    this.loadRemainingDimensions(dataSource);
  }

  loadRemainingDimensions(dataSource) {
    // Find images without dimensions (images 4+ that don't have thumbnail data)
    const unknownItems = dataSource.filter(item => item.width < 1 || item.height < 1);

    if (unknownItems.length === 0) return;

    // Load all remaining images in parallel
    unknownItems.forEach((item) => {
      const img = new Image();

      img.onload = () => {
        // Update the item with real dimensions
        item.width = img.naturalWidth;
        item.height = img.naturalHeight;

        // Update PhotoSwipe if this image is currently visible
        if (this.photoswipe.pswp && this.photoswipe.pswp.currIndex !== undefined) {
          const currentSlide = this.photoswipe.pswp.currSlide;
          // Check if this is the currently visible slide
          if (currentSlide && currentSlide.data.src === item.src) {
            this.photoswipe.pswp.refreshSlideContent(this.photoswipe.pswp.currIndex);
            this.photoswipe.pswp.updateSize();
          }
        }
      };

      img.onerror = () => {
        // Set fallback dimensions
        item.width = 800;
        item.height = 600;
      };

      img.src = item.src;
    });
  }
}

// Register the custom element
if (!window.customElements.get('product-image-galleries')) {
  window.customElements.define('product-image-galleries', ProductImageGalleries);
}

// Custom Shipping Calculator Web Component - adapted from theme's ShippingEstimator
class CustomShippingCalculator extends HTMLElement {
  // API endpoint for shipping rate calculations
  static SHIPPING_RATES_ENDPOINT = '/apps/integration/shipping-rates';

  constructor() {
    super();
    this._estimateShippingListener = this._estimateShipping.bind(this);
  }

  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.querySelector('.shipping-calculator__results');

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this._estimateShippingListener);
    }
  }

  disconnectedCallback() {
    if (this.submitButton) {
      this.submitButton.removeEventListener('click', this._estimateShippingListener);
    }
  }

  async _estimateShipping(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]')?.value;
    const country = this.querySelector('[name="address[country]"]')?.value;
    const province = this.querySelector('[name="address[province]"]')?.value;

    if (!zip || !country) {
      this._formatError({ general: 'Please fill in all required fields' });
      this.resultsElement.hidden = false;
      return;
    }

    // Get product and variant data
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;

    if (!productId || !variantId) {
      this._formatError({ general: 'Product information not found' });
      this.resultsElement.hidden = false;
      return;
    }

    // Get quantity from product form's quantity selector
    let quantity = 1; // Default fallback
    const quantitySelector = document.querySelector('quantity-selector input[name="quantity"]');
    if (quantitySelector && quantitySelector.value) {
      quantity = parseInt(quantitySelector.value) || 1;
    }

    this.submitButton.setAttribute('aria-busy', 'true');

    try {
      const body = JSON.stringify({
        variantId: variantId,
        quantity: quantity,
        currency: window.Shopify.currency.active,
        locale: window.Shopify.locale,
        shipping_address: { zip, country, province }
      });

      // Use the same custom backend as omhucph theme
      const response = await fetch(CustomShippingCalculator.SHIPPING_RATES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.shipping_rates) {
        this._formatShippingRates(data.shipping_rates);
      } else {
        this._formatError(data.error || { general: 'Unable to calculate shipping rates' });
      }
    } catch (error) {
      console.error('Shipping calculation error:', error);
      this._formatError({ general: 'Unable to calculate shipping rates. Please try again.' });
    } finally {
      this.resultsElement.hidden = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  _formatShippingRates(shippingRates) {
    let formattedShippingRates = shippingRates.map((shippingRate) => {
      return `<li>${shippingRate["presentment_name"] || shippingRate["name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
    });

    // Use the same strings as the theme's ShippingEstimator
    let message = '';
    if (shippingRates.length === 0) {
      message = window.themeVariables?.strings?.shippingEstimatorNoResults || 'Sorry, we do not ship to your address.';
    } else if (shippingRates.length === 1) {
      message = window.themeVariables?.strings?.shippingEstimatorOneResult || 'There is one shipping rate for your address:';
    } else {
      message = window.themeVariables?.strings?.shippingEstimatorMultipleResults || 'There are several shipping rates for your address:';
    }

    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${message}</p>
        ${formattedShippingRates.length > 0 ? `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>` : ''}
      </div>
    `;
  }

  _formatError(errors) {
    let formattedErrors = [];

    if (typeof errors === 'string') {
      formattedErrors = [`<li>${errors}</li>`];
    } else if (typeof errors === 'object') {
      formattedErrors = Object.keys(errors).map((errorKey) => {
        return `<li>${errors[errorKey]}</li>`;
      });
    }

    const errorMessage = window.themeVariables?.strings?.shippingEstimatorError || 'One or more error occurred while retrieving shipping rates:';

    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${errorMessage}</p>
        <ul class="list-disc" role="list">${formattedErrors.join('')}</ul>
      </div>
    `;
  }
}

if (!window.customElements.get("custom-shipping-calculator")) {
  window.customElements.define("custom-shipping-calculator", CustomShippingCalculator);
}

/* Draft order */
function raqCustomizeFormEmbed(hostElement) {
  const shadowRoot = hostElement.shadowRoot;
  if (!shadowRoot) {
    return;
  }

  /*
  const internalStyle = document.createElement('style');
  internalStyle.textContent = `
    #custom\\#product,
    #label-custom\\#product {
      display: none;
    }
  `;
  shadowRoot.appendChild(internalStyle);
  */

  // set accordion layout
  const appBlockHolder = hostElement.closest('.app-block-holder');
  if (appBlockHolder) {
    const appAccordionHolder = appBlockHolder.querySelector('.app-accordion-holder');
    if (appAccordionHolder) {
      var accordionTitle = '';

      const accordionTitleElement = appAccordionHolder.querySelector('.accordion__toggle span');
      if (accordionTitleElement) {
        var textHeadingElement = shadowRoot.querySelector('h2[class^="_textHeading_"]');
        if (textHeadingElement) {
          accordionTitle = textHeadingElement.innerText;
          accordionTitleElement.innerText = accordionTitle;
        }
      }

      if (accordionTitle != '') {
        const shopifyAppBlock = appBlockHolder.querySelector('.shopify-app-block');
        const accordionContentElement = appAccordionHolder.querySelector('.accordion__content');
        if (shopifyAppBlock && accordionContentElement) {
          accordionContentElement.appendChild(shopifyAppBlock);
        }

        appAccordionHolder.classList.remove('hidden');
      }
    }
  }

  const productInput = shadowRoot.querySelector('#custom\\#product');
  if (productInput) {
    var productTitle = '';
    var productTitleElement = document.querySelector('.product-info__title');
    if (productTitleElement) {
      productTitle = productTitleElement.innerText;
    }
    const productURL = window.location.href;

    productInput.value = productTitle + ': ' + productURL;
    productInput.dispatchEvent(new Event('input', { bubbles: true }));
    productInput.dispatchEvent(new Event('change', { bubbles: true }));

    productInput.parentElement.style.display = 'none';
  }

  const quantityInput = shadowRoot.querySelector('#custom\\#quantity');
  if (quantityInput) {
    var quantity = 1;

    const productFormQuantityInput = document.querySelector('.product-info__quantity-selector input[name="quantity"]');
    if (productFormQuantityInput) {
      quantity = productFormQuantityInput.value;

      productFormQuantityInput.addEventListener('input', (event) => {
        const newQuantity = event.target.value;
        quantityInput.value = newQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      productFormQuantityInput.addEventListener('change', (event) => {
        const newQuantity = event.target.value;
        quantityInput.value = newQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    quantityInput.value = quantity;
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    // quantityInput.parentElement.style.display = 'none';
  }
}

function raqStartObserving() {
  const container = document.querySelector('.shopify-section--main-product');
  if (!container) {
    return;
  }

  const observer = new MutationObserver((mutationsList, observer) => {
    const formEmbedHost = container.querySelector('#app-embed');
    if (formEmbedHost) {
      raqCustomizeFormEmbed(formEmbedHost);
      observer.disconnect();
    }
  });

  const config = { childList: true, subtree: true };
  observer.observe(container, config);
}
raqStartObserving();
/* end Draft order */

/* Raptor Recommendations Web Component */
class RaptorRecommendations extends HTMLElement {
  constructor() {
    super();
    this.raptorModule = this.getAttribute('raptor-module');
    this.recommendationsCount = parseInt(this.getAttribute('recommendations-count')) || 6;
    this.apiKey = this.getAttribute('api-key');
    this.customerId = this.getAttribute('customer-id');
    this.apiParams = this.getAttribute('api-params') || '';
    this.productSku = this.getAttribute('product-sku') || '';
    this.debugMode = this.getAttribute('debug-mode') === 'true';
    this.productList = this.querySelector('product-list');
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  }

  processApiParams() {
    if (!this.apiParams) return '';

    let processed = this.apiParams;

    // Replace [cookie:name] with actual cookie value
    const cookieRegex = /\[cookie:([^\]]+)\]/g;
    processed = processed.replace(cookieRegex, (match, cookieName) => {
      const cookieValue = this.getCookie(cookieName);
      if (this.debugMode) {
        console.log(`Raptor: Cookie ${cookieName} =`, cookieValue);
      }
      return encodeURIComponent(cookieValue);
    });

    // Replace [product.sku] with current variant SKU
    // Note: In Raptor's context, ProductId refers to variant SKU
    processed = processed.replace(/\[product\.sku\]/g, this.productSku);

    if (this.debugMode) {
      console.log('Raptor: Processed params:', processed);
    }
    return processed;
  }

  connectedCallback() {
    // Debug mode: use hardcoded test data
    if (this.debugMode) {
      console.log('Raptor Recommendations: Running in DEBUG mode');
      this.fetchRecommendationsDebug();
      return;
    }

    if (!this.apiKey || !this.customerId || !this.raptorModule) {
      this.showError('Raptor API credentials or module name not configured');
      return;
    }
    this.fetchRecommendations();
  }

  async fetchRecommendationsDebug() {
    try {
      // Test data with SKUs from your test set (matching actual Raptor response format)
      const testData = [
        {"RecommendedId":"AC04147", "Priority":"1"},
        {"RecommendedId":"AC04146", "Priority":"2"},
        {"RecommendedId":"AC04145", "Priority":"3"},
        {"RecommendedId":"AC04181", "Priority":"4"},
        {"RecommendedId":"AC04184", "Priority":"5"},
        {"RecommendedId":"AC04189", "Priority":"6"},
        {"RecommendedId":"AC04185", "Priority":"7"},
        {"RecommendedId":"AC04186", "Priority":"8"},
        {"RecommendedId":"AC04188", "Priority":"9"},
        {"RecommendedId":"AC04187", "Priority":"10"},
        {"RecommendedId":"AC04190", "Priority":"11"}
      ];

      console.log('Debug: Using test SKUs:', testData.map(item => item.RecommendedId));

      const productSkus = this.extractProductSkus(testData);

      if (productSkus.length === 0) {
        this.hideSection();
        return;
      }

      await this.fetchAndRenderProducts(productSkus);
    } catch (error) {
      console.error('Raptor recommendations debug error:', error);
      this.showError('Unable to load recommendations (debug mode)');
    }
  }

  async fetchRecommendations() {
    try {
      // Build base URL
      let raptorUrl = `https://api.raptorsmartadvisor.com/v1/${this.customerId}/${this.raptorModule}/${this.recommendationsCount}/${this.apiKey}`;

      // Add query parameters if configured
      const params = this.processApiParams();
      if (params) {
        raptorUrl += `?${params}`;
      }

      const raptorResponse = await fetch(raptorUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!raptorResponse.ok) {
        throw new Error(`Raptor API error: ${raptorResponse.status}`);
      }

      const raptorData = await raptorResponse.json();

      if (!raptorData || raptorData.length === 0) {
        this.hideSection();
        return;
      }

      const productSkus = this.extractProductSkus(raptorData);

      if (productSkus.length === 0) {
        this.hideSection();
        return;
      }

      await this.fetchAndRenderProducts(productSkus);
    } catch (error) {
      console.error('Raptor recommendations error:', error);
      this.showError('Unable to load recommendations');
    }
  }

  extractProductSkus(raptorData) {
    const skus = [];

    for (const item of raptorData) {
      // Raptor returns RecommendedId which contains the variant SKU
      if (item.RecommendedId) {
        skus.push(item.RecommendedId);
      }
    }

    return skus;
  }

  async fetchAndRenderProducts(skus) {
    const searchQuery = skus.map(sku => `variants.sku:${sku}`).join(' OR ');
    const searchUrl = `${window.Shopify.routes.root}search?view=raptor&type=product&q=${encodeURIComponent(searchQuery)}`;

    if (this.debugMode) {
      console.log('Raptor: Searching for', skus.length, 'products');
      console.log('Raptor: Search query:', searchQuery);
      console.log('Raptor: Search URL:', searchUrl);
    }

    const response = await fetch(searchUrl);

    if (this.debugMode) {
      console.log('Raptor: Search response status:', response.status);
    }

    if (!response.ok) {
      throw new Error(`Search API error: ${response.status}`);
    }

    const html = await response.text();

    if (this.debugMode) {
      console.log('Raptor: Received HTML length:', html.length);
      console.log('Raptor: HTML preview:', html.substring(0, 200));
    }

    if (html.trim() === '') {
      if (this.debugMode) {
        console.warn('Raptor: Empty search results, hiding section');
      }
      this.hideSection();
      return;
    }

    this.productList.innerHTML = html;

    if (this.debugMode) {
      console.log('Raptor: Products rendered successfully');
    }

    // Add click tracking to recommendation links
    this.attachClickTracking();

    const scrollCarousel = this.closest('.floating-controls-container, .scrollable-with-controls')?.querySelector('scroll-carousel');
    if (scrollCarousel) {
      scrollCarousel.classList.add('is-scrollable');
    }
  }

  attachClickTracking() {
    const recommendationItems = this.productList.querySelectorAll('.raptor-recommendation-item');

    recommendationItems.forEach((item) => {
      const productSku = item.dataset.productSku;
      const productUrl = item.dataset.productUrl;

      // Find all links within this recommendation item
      const links = item.querySelectorAll('a');

      links.forEach((link) => {
        link.addEventListener('click', (e) => {
          // Only track if we have the Shopify analytics API and required data
          if (window.Shopify && window.Shopify.analytics && productSku && this.raptorModule) {
            e.preventDefault();

            if (this.debugMode) {
              console.log('Raptor: Tracking recommendation click', {
                productId: productSku,
                moduleName: this.raptorModule,
                productUrl: productUrl
              });
            }

            // Publish custom event to Shopify pixel
            window.Shopify.analytics.publish('custom_recommendation_click', {
              productId: productSku,
              moduleName: this.raptorModule,
              productUrl: productUrl
            });

            // Fallback redirect if pixel doesn't handle it
            setTimeout(() => {
              window.location.href = link.href;
            }, 300);
          }
        });
      });
    });
  }

  showError(message) {
    if (this.productList) {
      this.productList.innerHTML = `<div class="error-message"><p>${message}</p></div>`;
    }
  }

  hideSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = 'none';
    }
  }
}

customElements.define('raptor-recommendations', RaptorRecommendations);
/* end Raptor Recommendations */

/* Exploded View Lightbox - Single image PhotoSwipe overlay */
import { PhotoSwipeLightbox as ExplodedViewPhotoSwipe } from "vendor";

class ExplodedViewLightbox extends HTMLElement {
  constructor() {
    super();
    this._photoswipe = null;
    this._boundHandleClick = this.handleClick.bind(this);
    this._boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    const trigger = this.querySelector('.exploded-view-thumbnail');
    if (!trigger) return;

    trigger.addEventListener('click', this._boundHandleClick);
    trigger.addEventListener('keydown', this._boundHandleKeydown);
  }

  disconnectedCallback() {
    const trigger = this.querySelector('.exploded-view-thumbnail');
    if (trigger) {
      trigger.removeEventListener('click', this._boundHandleClick);
      trigger.removeEventListener('keydown', this._boundHandleKeydown);
    }

    // Clean up PhotoSwipe instance
    if (this._photoswipe) {
      this._photoswipe.destroy();
      this._photoswipe = null;
    }
  }

  handleClick(event) {
    event.preventDefault();
    this.openLightbox();
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openLightbox();
    }
  }

  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }

    // Match exact theme PhotoSwipe configuration
    const photoswipe = new ExplodedViewPhotoSwipe({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables?.strings?.closeGallery || 'Close gallery',
      zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
      errorMsg: window.themeVariables?.strings?.errorGallery || 'Image cannot be loaded',
      // UX - match theme settings exactly
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    // Handle unknown image dimensions (fallback for when aspect ratio can't be determined)
    photoswipe.on('gettingData', (item) => {
      const slideData = item.data;
      if (slideData.width < 1 || slideData.height < 1) {
        const img = new Image();
        img.onload = function() {
          slideData.width = this.width;
          slideData.height = this.height;
          if (photoswipe.pswp) {
            photoswipe.pswp.refreshSlideContent(photoswipe.pswp.currIndex);
            photoswipe.pswp.updateSize();
          }
        };
        img.onerror = function() {
          // Fallback dimensions
          slideData.width = 1200;
          slideData.height = 900;
        };
        img.src = slideData.src;
      }
    });

    photoswipe.init();
    return this._photoswipe = photoswipe;
  }

  openLightbox() {
    const fullSrc = this.dataset.fullSrc;

    // Edge case: no source URL
    if (!fullSrc) {
      console.warn('ExplodedViewLightbox: No full source URL provided');
      return;
    }

    const thumbnailImg = this.querySelector('.exploded-view-image');

    // Calculate dimensions - use thumbnail natural dimensions or fallback
    let width = 2000;
    let height = 1500; // Default 4:3 aspect ratio fallback

    if (thumbnailImg && thumbnailImg.naturalWidth > 0 && thumbnailImg.naturalHeight > 0) {
      const aspectRatio = thumbnailImg.naturalWidth / thumbnailImg.naturalHeight;
      height = Math.round(width / aspectRatio);
    }

    const dataSource = [{
      src: fullSrc,
      width: width,
      height: height,
      alt: thumbnailImg?.alt || 'Exploded view diagram',
      // Provide thumbnail element for smooth animation
      thumbnailElement: thumbnailImg
    }];

    this.photoswipe.loadAndOpen(0, dataSource);
  }
}
customElements.define('exploded-view-lightbox', ExplodedViewLightbox);
/* end Exploded View Lightbox */


// Separate price decimals
function formatPriceNodes() {
  const elements = document.querySelectorAll(
    '.product-info__purchase-card .normal-price-section .text-lg, .product-info__purchase-card .member-price-red'
  );
  elements.forEach(function (el) {
    // Add class to the existing "incl. VAT" span (if present)
    el.querySelectorAll('span.text-xs.text-subdued').forEach(function (span) {
      span.classList.add('price-vat-note');
    });
    // Prevent double processing
    if (el.querySelector('.price-decimals')) return;
    // Find the first meaningful TEXT node inside the element
    let textNode = null;
    for (let i = 0; i < el.childNodes.length; i++) {
      const n = el.childNodes[i];
      if (
        n.nodeType === Node.TEXT_NODE &&
        n.nodeValue &&
        n.nodeValue.replace(/\s+/g, '').length
      ) {
        textNode = n;
        break;
      }
    }
    if (!textNode) return;
    const raw = (textNode.nodeValue || '').replace(/\s+/g, ' ').trim();
    // Match: whole + separator + 2 decimals + optional currency
    const m = raw.match(/^(.+?)([.,])(\d{2})(\s*(?:[^\d\s].*|[A-Z]{3})\s*)?$/);
    if (!m) return;
    const whole = m[1].trim();
    const sep = m[2];
    const dec = m[3];
    const currency = (m[4] || '').replace(/\s+/g, ' ');
    // Replace only that text node with:
    // whole + <span class="price-decimals">,44 €</span>
    const decimalsSpan = document.createElement('span');
    decimalsSpan.className = 'price-decimals';
    decimalsSpan.textContent = sep + dec + currency;
    el.insertBefore(document.createTextNode(whole), textNode);
    el.insertBefore(decimalsSpan, textNode);
    el.removeChild(textNode);
  });
}
// Run on DOM ready
document.addEventListener('DOMContentLoaded', formatPriceNodes);
// Optional: expose to rerun after variant/price updates
window.formatPriceNodes = formatPriceNodes;
// End separate price decimals


// Swap .product-price-member-badge with its next sibling element (move it after the next element)
/*
function swapMemberBadgeWithNextSibling() {
  const badges = document.querySelectorAll('.product-price-member-badge');
  badges.forEach(function (badge) {
    // next *element* sibling (skips text nodes + comments)
    const nextEl = badge.nextElementSibling;
    if (!nextEl) return;
    // Move badge after the next element
    nextEl.insertAdjacentElement('afterend', badge);
  });
}
document.addEventListener('DOMContentLoaded', swapMemberBadgeWithNextSibling);
window.swapMemberBadgeWithNextSibling = swapMemberBadgeWithNextSibling;
*/
